/**
 * Requirement decisions and gate stages.
 *
 * Contract: docs/source-of-truth/worker-service-delivery-architecture.md
 * ("Requirement decision engine").
 *
 * Principle 6 — treat absence honestly. `NOT_APPLICABLE_BY_DESIGN`,
 * `KNOWN_NONE`, `WITHHELD`, `UNAVAILABLE`, `STALE`, `MISSING` and `FAILED` are
 * distinct facts with distinct operational routing. Collapsing them into a
 * boolean is the defect this module exists to prevent: "the client has no
 * allergies" and "nobody has ever asked" must not render identically.
 */

import type { RequirementAuthority } from './authority';
import { isTenantWaivableAuthority, supportsExactSlotException } from './authority';

// ---------------------------------------------------------------------------
// Gate stages
// ---------------------------------------------------------------------------

/**
 * Ordered earliest-first. Passing an earlier stage never satisfies a later one:
 * schedule-time success is not point-of-care authority.
 */
export const GATE_STAGES = [
  'PLAN_READY',
  'SCHEDULE',
  'ASSIGN',
  'MAKE_VISIBLE',
  'START',
  'ACTION',
  'COMPLETE',
  'FINANCE_PROJECT',
] as const;

export type GateStage = (typeof GATE_STAGES)[number];

const GATE_STAGE_SET: ReadonlySet<string> = new Set(GATE_STAGES);

export function isGateStage(value: string | null | undefined): value is GateStage {
  return typeof value === 'string' && GATE_STAGE_SET.has(value);
}

export function gateStageRank(stage: GateStage): number {
  return GATE_STAGES.indexOf(stage);
}

/**
 * Stages that must be re-evaluated server-side at the moment of use, never
 * served from a cached earlier decision. A credential or delegation can expire
 * between assignment and the worker's arrival.
 */
const ALWAYS_REEVALUATED_STAGES: ReadonlySet<GateStage> = new Set([
  'START',
  'ACTION',
  'COMPLETE',
  'FINANCE_PROJECT',
]);

export function requiresPointOfUseReevaluation(stage: GateStage): boolean {
  return ALWAYS_REEVALUATED_STAGES.has(stage);
}

// ---------------------------------------------------------------------------
// Decisions
// ---------------------------------------------------------------------------

export const REQUIREMENT_DECISIONS = [
  'PASS',
  'NOT_APPLICABLE_BY_DESIGN',
  'KNOWN_NONE',
  'UNAVAILABLE',
  'MISSING',
  'FAILED',
  'EXPIRED',
  'STALE',
  'WITHHELD',
  'EXCEPTION_ACCEPTED',
  'SUPERVISOR_REVIEW_REQUIRED',
] as const;

export type RequirementDecision = (typeof REQUIREMENT_DECISIONS)[number];

const REQUIREMENT_DECISION_SET: ReadonlySet<string> = new Set(REQUIREMENT_DECISIONS);

export function isRequirementDecision(
  value: string | null | undefined,
): value is RequirementDecision {
  return typeof value === 'string' && REQUIREMENT_DECISION_SET.has(value);
}

/**
 * How a decision behaves at a gate, without discarding which decision it was.
 *
 * - `SATISFIED`    — the requirement is met or does not apply.
 * - `DEFICIENT`    — the requirement is not met. Blocking depends on authority.
 * - `DEFERRED`     — routed to a human; not a pass and not a plain failure.
 */
export const DECISION_CLASSES = ['SATISFIED', 'DEFICIENT', 'DEFERRED'] as const;

export type DecisionClass = (typeof DECISION_CLASSES)[number];

const DECISION_CLASS_BY_DECISION: Readonly<Record<RequirementDecision, DecisionClass>> = {
  PASS: 'SATISFIED',
  // The requirement genuinely does not apply to this exact service/mode/actor.
  NOT_APPLICABLE_BY_DESIGN: 'SATISFIED',
  // A positive, recorded "there is none" — e.g. a completed nil-allergy record.
  KNOWN_NONE: 'SATISFIED',
  // An audited, expiring, exact-slot provider-policy exception.
  EXCEPTION_ACCEPTED: 'SATISFIED',
  // The datum exists but could not be read now (offline, outage, permission).
  UNAVAILABLE: 'DEFICIENT',
  // Nobody has recorded it. Distinct from KNOWN_NONE.
  MISSING: 'DEFICIENT',
  // Recorded and does not meet the requirement.
  FAILED: 'DEFICIENT',
  // Was satisfied; validity has lapsed.
  EXPIRED: 'DEFICIENT',
  // Cached beyond the freshness policy for this datum.
  STALE: 'DEFICIENT',
  // Deliberately withheld from this actor's role projection.
  WITHHELD: 'DEFICIENT',
  SUPERVISOR_REVIEW_REQUIRED: 'DEFERRED',
};

export function decisionClass(decision: RequirementDecision): DecisionClass {
  return DECISION_CLASS_BY_DECISION[decision];
}

export function isSatisfiedDecision(decision: RequirementDecision): boolean {
  return decisionClass(decision) === 'SATISFIED';
}

/**
 * Decisions that assert positive knowledge, as opposed to the absence of
 * knowledge. Only these may render as settled to a worker; the deficient set
 * must render as an unresolved gap.
 *
 * This groups the two by epistemic status, and callers must still branch on
 * `decision` for the wording. `KNOWN_NONE` means "asked and there is none"
 * ("no known allergies"); `NOT_APPLICABLE_BY_DESIGN` means "this does not apply
 * to this service" ("allergy check not part of this service"). Rendering them
 * with the same sentence is the collapse principle 6 forbids.
 */
const POSITIVE_ABSENCE_DECISIONS: ReadonlySet<RequirementDecision> = new Set([
  'KNOWN_NONE',
  'NOT_APPLICABLE_BY_DESIGN',
]);

export function isPositiveAbsence(decision: RequirementDecision): boolean {
  return POSITIVE_ABSENCE_DECISIONS.has(decision);
}

// ---------------------------------------------------------------------------
// Blocking
// ---------------------------------------------------------------------------

/**
 * What a (decision, authority) pair does to a gate.
 *
 * This is the single derivation. `isBlockingDecision`, `resolveRequirement` and
 * `evaluateGate` all route through it so that a gate can never be told one
 * thing while a stored record says another.
 *
 * - `CLEAR`  — nothing to stop; satisfied, or advisory under OPTIONAL_CAPTURE.
 * - `REVIEW` — a named human must decide. Not a pass and not a plain failure.
 * - `BLOCK`  — the requirement is unmet and delivery cannot proceed.
 */
export const REQUIREMENT_DISPOSITIONS = ['CLEAR', 'REVIEW', 'BLOCK'] as const;

export type RequirementDisposition = (typeof REQUIREMENT_DISPOSITIONS)[number];

export function requirementDisposition(
  decision: RequirementDecision,
  authority: RequirementAuthority,
): RequirementDisposition {
  // Advice never blocks and never escalates. A capture that genuinely needs a
  // supervisor is not optional, and `decisionPermittedForAuthority` refuses to
  // build that pairing in the first place; this arm keeps an untrusted or
  // replayed record from failing a gate on an advisory item.
  if (authority === 'OPTIONAL_CAPTURE') return 'CLEAR';
  switch (decisionClass(decision)) {
    case 'SATISFIED':
      return 'CLEAR';
    case 'DEFERRED':
      return 'REVIEW';
    case 'DEFICIENT':
      return 'BLOCK';
  }
}

/**
 * Whether a decision stops delivery outright, given the authority that owns it.
 *
 * `OPTIONAL_CAPTURE` never blocks — it is advice. Every other authority blocks
 * when deficient; the difference between them is whether an exception could
 * ever have been granted in the first place (see `supportsExactSlotException`),
 * not whether the unexcepted deficiency is tolerable.
 *
 * `EXCEPTION_ACCEPTED` is classified `SATISFIED`, so an accepted exception
 * reaches this function as a pass. An authority that cannot carry an exception
 * must therefore never be allowed to *produce* `EXCEPTION_ACCEPTED`; that
 * invariant is asserted by `decisionPermittedForAuthority`.
 */
export function isBlockingDecision(
  decision: RequirementDecision,
  authority: RequirementAuthority,
): boolean {
  return requirementDisposition(decision, authority) === 'BLOCK';
}

/**
 * Guards the combinations that would silently defeat the model.
 *
 * Returns an error reason, or `null` when the pairing is legitimate.
 */
export function decisionPermittedForAuthority(
  decision: RequirementDecision,
  authority: RequirementAuthority,
): string | null {
  if (decision === 'EXCEPTION_ACCEPTED') {
    if (!isTenantWaivableAuthority(authority)) {
      return `authority_not_waivable:${authority}`;
    }
    // Waivable is necessary but not sufficient. Only PROVIDER_POLICY has the
    // enumerated, exact-slot, expiring manager exception. A service contract or
    // a care-plan instruction is changed through its own governed authoring
    // path, never excepted away at the point of care.
    if (!supportsExactSlotException(authority)) {
      return `authority_has_no_exception_path:${authority}`;
    }
  }
  if (decision === 'SUPERVISOR_REVIEW_REQUIRED' && authority === 'OPTIONAL_CAPTURE') {
    // Advisory capture cannot demand a supervisor. If it genuinely does, the
    // requirement belongs at PROVIDER_POLICY or above.
    return 'optional_capture_cannot_require_supervisor_review';
  }
  if (decision === 'WITHHELD' && authority === 'STATUTORY') {
    // A statutory obligation cannot be hidden from the actor who must meet it.
    return 'statutory_requirement_cannot_be_withheld';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Resolved requirement
// ---------------------------------------------------------------------------

export interface ResolvedRequirement {
  /** Stable machine code, e.g. `RN_REGISTRATION_CURRENT`. */
  code: string;
  authority: RequirementAuthority;
  stage: GateStage;
  decision: RequirementDecision;
  /** Derived, not caller-supplied. See `isBlockingDecision`. */
  blocking: boolean;
  /** PHI-minimised routing code shown to worker/coordinator. */
  reasonCode: string;
  sourceType: string;
  sourceId?: string;
  sourceVersion?: string;
  effectiveAt: string;
  evaluatedAt: string;
  expiresAt?: string;
  exceptionId?: string;
}

/**
 * Builds a `ResolvedRequirement` with `blocking` derived rather than trusted,
 * and rejects an authority/decision pairing the model forbids.
 */
export function resolveRequirement(
  input: Omit<ResolvedRequirement, 'blocking'>,
): ResolvedRequirement {
  const violation = decisionPermittedForAuthority(input.decision, input.authority);
  if (violation) {
    throw new Error(`invalid_requirement_decision:${violation}`);
  }
  if (input.decision === 'EXCEPTION_ACCEPTED' && !input.exceptionId) {
    throw new Error('invalid_requirement_decision:exception_accepted_requires_exception_id');
  }
  return {
    ...input,
    blocking: isBlockingDecision(input.decision, input.authority),
  };
}

/**
 * A gate passes only when nothing blocking remains at or before that stage.
 * Deferred (`SUPERVISOR_REVIEW_REQUIRED`) is reported separately so the caller
 * can route to review rather than showing a flat failure.
 */
export interface GateOutcome {
  stage: GateStage;
  passed: boolean;
  blocking: readonly ResolvedRequirement[];
  awaitingReview: readonly ResolvedRequirement[];
}

export function evaluateGate(
  stage: GateStage,
  requirements: readonly ResolvedRequirement[],
): GateOutcome {
  const inScope = requirements.filter((r) => gateStageRank(r.stage) <= gateStageRank(stage));
  // The stored `blocking` flag is never trusted here. A requirement can reach
  // this function from an offline replay, a cached snapshot or any other
  // deserialised source, so a forged or stale `blocking: false` on a failed
  // statutory requirement must not be able to open the gate. Disposition is
  // re-derived from the two facts that carry authority: decision and authority.
  const blocking = inScope.filter(
    (r) => requirementDisposition(r.decision, r.authority) === 'BLOCK',
  );
  const awaitingReview = inScope.filter(
    (r) => requirementDisposition(r.decision, r.authority) === 'REVIEW',
  );
  return {
    stage,
    passed: blocking.length === 0 && awaitingReview.length === 0,
    blocking,
    awaitingReview,
  };
}
